import { SquareAutocompleteInput } from 'components/v2/squareAutocompleteInput/SquareAutocompleteInput';
import Button from 'components/v2/button/Button';
import { useState, useMemo, ReactNode } from 'react';
import { cn } from 'utils/helpers';
import EmojiAvatar from 'modules/home/pages/homePage/EmojiAvatar';
import { Contact } from 'modules/profile/types/types';
import { Check } from 'lucide-react';
import { useAuth } from 'modules/auth/context/AuthContextV2';

const CreateListPopup: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}> = ({ isOpen, setIsOpen }) => {
  const { user } = useAuth();
  const [listName, setListName] = useState<string>('');
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [fetchedContacts, setFetchedContacts] = useState<Contact[]>(
    user?.contacts ?? [],
  );
  const [eventContacts, setEventContacts] = useState<Contact[]>([]);

  const handleContactClick = (contact: Contact) => {
    setSelectedContacts((prev) => {
      if (
        prev.some((selected) => selected.phoneNumber === contact.phoneNumber)
      ) {
        return prev.filter(
          (selected) => selected.phoneNumber !== contact.phoneNumber,
        );
      } else {
        return [...prev, contact];
      }
    });
  };

  const sortedContacts = useMemo(() => {
    return [...fetchedContacts].sort((a, b) => a.name.localeCompare(b.name));
  }, [fetchedContacts, selectedContacts]);

  return (
    <div
      className={`w-full flex flex-col justify-end items-center pointer-events-none fixed inset-0 z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
    >
      {isOpen && (
        <div
          className="absolute inset-0 bg-[#0A0118] opacity-70 pointer-events-auto"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div
        className={`w-full flex flex-col gap-5 items-center relative z-10 p-5 bg-[#0A0118] rounded-[1rem] ${
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        <div className="text-white text-center text-[1.3rem] font-semibold">
          <div className="flex flex-col gap-2">
            Create a Contact List
            <div className="text-white/80 text-center text-[0.8rem] font-normal">
              Please enter a name for your new contact list
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <SquareAutocompleteInput
            dropdownLabel="List Name"
            defaultEmoji="📝"
            dropdownEmptyLabel="No lists found"
            placeholder="Enter list name..."
            aria-label="List name input"
            label="List Name"
            editInputValue={listName}
            onInputValueChange={(value) => setListName(value)}
            options={[]}
            selectedOption={undefined}
            setSelectedOption={() => {}}
          />
        </div>{' '}
        <div className="mb-4 overflow-y-scroll max-h-[20rem]">
          <div className="flex flex-col gap-2">
            {sortedContacts.map((contact, index) => {
              const isSelected = selectedContacts.some(
                (selected) => selected.phoneNumber === contact.phoneNumber,
              );
              const isEventContact = eventContacts.some(
                (eventContact) =>
                  eventContact.phoneNumber === contact.phoneNumber,
              );
              return (
                <div
                  key={index}
                  onClick={() => {
                    if (isEventContact) {
                      return;
                    }
                    handleContactClick(contact);
                  }}
                  className={cn(
                    'flex items-center justify-between gap-4 px-5 py-4 rounded-[1rem] cursor-pointer',
                    isSelected ? 'bg-[#2A2A4D]' : 'bg-[#110E2B]',
                    isEventContact && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <div className="flex items-center gap-4 w-full">
                    <div className="relative">
                      <EmojiAvatar
                        fullName={contact.name}
                        image={`${process.env.REACT_APP_BASE_URL}/users/profile-pic/${contact.userId}`}
                      />
                    </div>
                    <div className="flex flex-col justify-center items-start">
                      <div className="flex gap-1 text-xs font-semibold text-center truncate contact-search-name">
                        <div className="text-[0.875rem] font-semibold">
                          {contact.name.split(' ')[0]}
                        </div>
                      </div>
                      <div className="text-[0.875rem] text-[rgb(255,255,255,0.8)] font-normal text-center">
                        {contact.name}
                      </div>
                    </div>{' '}
                  </div>
                  {(isSelected || isEventContact) && (
                    <Check className="text-[#ABE49C] w-4 h-4" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <Button
            variant="secondary"
            className="w-full px-6 py-3 whitespace-nowrap"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="w-full px-6 py-3 whitespace-nowrap"
            disabled={selectedContacts.length === 0 || listName === ''}
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Create List
          </Button>
        </div>
      </div>
    </div>
  );
};
export default CreateListPopup;
